(() => {
  class AgHeaderMenu extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-ag-menu-toggle]');
        if (toggle && this.contains(toggle)) this.setExpanded(toggle, toggle.getAttribute('aria-expanded') !== 'true');
      }, { signal });
      this.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape') return;
        const item = event.target.closest('.ag-header-menu__item');
        const ownToggle = item?.querySelector(':scope > .ag-header-menu__row > [data-ag-menu-toggle]');
        const panel = event.target.closest('.ag-header-menu__panel');
        const toggle = ownToggle?.getAttribute('aria-expanded') === 'true'
          ? ownToggle
          : panel?.previousElementSibling.querySelector('[data-ag-menu-toggle]');
        if (!toggle) return;
        event.stopPropagation();
        this.setExpanded(toggle, false);
        toggle.focus();
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.closeAll();
      }, { signal });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.closeAll();
      }, { signal });
    }

    setExpanded(toggle, expanded) {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel || !this.contains(panel)) return;
      if (expanded) {
        const item = toggle.closest('.ag-header-menu__item');
        Array.from(item.parentElement.children).forEach((sibling) => {
          if (sibling === item) return;
          sibling.querySelectorAll('[data-ag-menu-toggle]').forEach((other) => this.setExpanded(other, false));
        });
      } else {
        panel.querySelectorAll('[data-ag-menu-toggle]').forEach((child) => this.setExpanded(child, false));
        if (panel.contains(document.activeElement)) toggle.focus();
      }
      toggle.setAttribute('aria-expanded', String(expanded));
      panel.hidden = !expanded;
    }

    closeAll() {
      this.querySelectorAll('[data-ag-menu-toggle][aria-expanded="true"]').forEach((toggle) => this.setExpanded(toggle, false));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.closeAll();
    }
  }

  class AgHeaderDrawer extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.details = this.querySelector('details');
      this.summary = this.details.querySelector('summary');
      this.header = this.closest('.section-header');
      this.summary.setAttribute('aria-expanded', String(this.details.open));
      this.summary.addEventListener('click', (event) => {
        event.preventDefault();
        this.details.open ? this.close() : this.open();
      }, { signal });
      this.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape' || !this.details.open) return;
        event.stopPropagation();
        this.close();
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || !this.details.open) return;
        const elements = Array.from(this.details.querySelectorAll('summary, a[href], button:enabled, input:not([type="hidden"]):enabled, [tabindex="0"]'))
          .filter((element) => element.tabIndex >= 0 && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (this.details.open && event.relatedTarget && !this.contains(event.relatedTarget)) this.close(false);
      }, { signal });
      this.details.addEventListener('toggle', () => {
        if (!this.details.open) this.close(false);
      }, { signal });
      window.addEventListener('resize', () => {
        if (!this.details.open) return;
        if (window.matchMedia('(min-width: 1024px)').matches) {
          const hadFocus = this.contains(document.activeElement);
          this.close(false);
          if (hadFocus) this.header?.querySelector('.header__ag-nav a')?.focus();
        } else {
          this.updatePosition();
        }
      }, { signal });
    }

    updatePosition() {
      document.documentElement.style.setProperty('--header-bottom-position', `${this.header?.getBoundingClientRect().bottom || 0}px`);
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }

    open() {
      this.updatePosition();
      this.details.open = true;
      this.summary.setAttribute('aria-expanded', 'true');
      this.header?.classList.add('menu-open');
      document.body.classList.add('overflow-hidden-tablet');
      this.frame = requestAnimationFrame(() => this.details.classList.add('menu-opening'));
      this.summary.focus();
    }

    close(restoreFocus = true) {
      cancelAnimationFrame(this.frame);
      this.querySelectorAll('ag-header-menu').forEach((menu) => menu.closeAll());
      this.details.classList.remove('menu-opening');
      this.summary.setAttribute('aria-expanded', 'false');
      this.header?.classList.remove('menu-open');
      document.body.classList.remove('overflow-hidden-tablet');
      this.details.open = false;
      if (restoreFocus) this.summary.focus();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.close(false);
    }
  }

  if (!customElements.get('ag-header-menu')) customElements.define('ag-header-menu', AgHeaderMenu);
  if (!customElements.get('ag-header-drawer')) customElements.define('ag-header-drawer', AgHeaderDrawer);
})();
